import axios, { Method } from 'axios';
import { Executable, IPrepareRequestOptions } from '../executable';
import {
  IWalletTemplateUpdateInput,
  IWalletTemplatePassData,
  IWalletTemplateAndroidPassData,
  IWalletTemplateFieldMapping,
  BambuPassStyles,
} from '../interfaces/wallet-template';
import { UploadTemporaryFile } from './upload-temporary-file';

// had to make some hack to make the module be imported
let fileTypeModule: any;

export interface IWalletTemplateUpdateResponse {
  templateId: number;
  message: string;
}

export interface IWalletTemplateUpdateExtendedInput extends IWalletTemplateUpdateInput {
  brandId: number;
  programId: number;
}

export class UpdateWalletTemplate
  extends Executable<IWalletTemplateUpdateResponse>
  implements Partial<IWalletTemplateUpdateExtendedInput>
{
  public brandId?: number;

  public programId?: number;

  public name?: string;

  public organizationName?: string;

  public sharingProhibited?: boolean;

  public sharingUrl?: string;

  public passdata?: IWalletTemplatePassData;

  public androidPassData?: IWalletTemplateAndroidPassData;

  public fieldMappings?: IWalletTemplateFieldMapping[];

  public walletIdentifierId?: number;

  public defaultMessage?: string;

  public getMethod(): Method {
    return 'patch';
  }

  public async prepareRequest({ execute }: IPrepareRequestOptions): Promise<any> {
    if (!fileTypeModule) {
      fileTypeModule = await import('file-type');
    }

    const appleImages =
      this?.passdata?.images?.filter(
        (image) => typeof image.imageFile !== 'string' || !image.imageFile.startsWith('https:')
      ) || [];
    const androidImages =
      this?.androidPassData?.images?.filter(
        (image) => typeof image.imageFile !== 'string' || !image.imageFile.startsWith('https:')
      ) || [];
    const allImagesRaw = [...appleImages, ...androidImages];

    const allImages = [] as {
      image: string;
      imageFile: Buffer;
      contentType: string;
      type: 'apple' | 'android';
    }[];

    for (let i = 0; i < allImagesRaw.length; i++) {
      const image = allImagesRaw[i];
      const imageBuffer =
        image.imageFile instanceof Buffer
          ? image.imageFile
          : Buffer.from(image.imageFile, 'base64');
      const filetype = await fileTypeModule.fileTypeFromBuffer(imageBuffer);

      if (filetype) {
        allImages.push({
          image: image.image,
          imageFile: imageBuffer,
          contentType: filetype.mime,
          type: i < appleImages.length ? 'apple' : 'android',
        });
      } else {
        throw new Error(
          `Could not extract image type from ${
            i < appleImages.length ? 'apple' : 'android'
          } image "${image.image}"`
        );
      }
    }

    const uploadLinks = await execute(
      new UploadTemporaryFile({
        brandId: this.brandId,
        amount: allImages.length,
      })
    );

    const uploadMap = {} as { [key: string]: string };

    for (let i = 0; i < allImages.length; i++) {
      const { uploadUrl, key } = uploadLinks[i];
      await axios.put(uploadUrl, allImages[i].imageFile, {
        headers: {
          'Content-Type': allImages[i].contentType,
        },
      });
      uploadMap[`${allImages[i].type}_${allImages[i].image}`] = key;
    }

    for (const image of allImages) {
      if (image.type === 'android' && this.androidPassData?.images) {
        const originalImage = this.androidPassData.images.find((i) => i.image === image.image);
        if (originalImage) {
          originalImage.imageFile = uploadMap[`android_${image.image}`];
        }
      } else {
        const originalImage = this.passdata?.images.find((i) => i.image === image.image);
        if (originalImage) {
          originalImage.imageFile = uploadMap[`apple_${image.image}`];
        }
      }
    }
  }

  public getResponse(apiResponse: any): IWalletTemplateUpdateResponse {
    return apiResponse;
  }

  public getURLPath(tenantId?: string): string {
    return `${this.brandId}/programs/${this.programId}/templates`;
  }

  constructor(
    input?: Partial<IWalletTemplateUpdateExtendedInput> & { tenantId: number; name: string }
  ) {
    super();
    if (input) {
      Object.assign(this, input);
    }
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): Partial<IWalletTemplateUpdateExtendedInput> {
    return {
      name: this.name,
      organizationName: this.organizationName,
      sharingProhibited: this.sharingProhibited,
      sharingUrl: this.sharingUrl,
      passdata: this.passdata,
      androidPassData: this.androidPassData,
      fieldMappings: this.fieldMappings,
      walletIdentifierId: this.walletIdentifierId,
      defaultMessage: this.defaultMessage,
    };
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return 'brands';
  }
}

export { BambuPassStyles };
